import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { displayError } from '@/lib/error';
import { toast } from '@/components/ui/use-toast';
import { useUserStore } from '@/stores/user';
import { hasFeature } from '@/services/featureService';
import { isAdminOrSupervisor, isDueDiligenceTeamMember, canSelectDdiAssigneeOnForm } from '@/services/userService';
import {
  assignDdiWorkOrder,
  cancelDdiWorkOrderEdit,
  useFetchDdiWorkOrderForEdit,
  useUpdateDdiWorkOrder,
} from '@/services/dueDiligenceService';
import { Feature } from '@/utils/constant';
import { DDIWorkOrderCreateStep1 } from './DDIWorkOrderCreateStep1';
import { DDIWorkOrderCreateStep2 } from './DDIWorkOrderCreateStep2';
import {
  applyZodIssuesToForm,
  buildDdiWorkOrderUpdatePayload,
  DdiStep1Schema,
  DdiStep2Schema,
  mapDdiEditResponseToFormValues,
  type DdiCreateFormValues,
} from './ddiWorkOrderCreate.schema';
import type { GenericErrorResponse } from '@/types/api.types';

const STEPS = [
  { title: 'Case Details', isCompleted: false },
  { title: 'Search Details', isCompleted: false },
];

const STEP_SCHEMAS = [DdiStep1Schema, DdiStep2Schema];

/** User-facing copy for API subtype/report compatibility errors. */
const DDI_SUBTYPE_REPORT_COMPATIBILITY_MESSAGE =
  "You changed the DDI subtype. Please select a new report that's compatible with the selected subtype.";

const isLockConflictError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const err = error as GenericErrorResponse;
  const statusCode = err.error?.statusCode;
  const code = err.error?.code;
  return statusCode === 409 || code === 'CONFLICT';
};

const getApiErrorText = (error: unknown): string => {
  if (!error || typeof error !== 'object') return '';
  const err = error as GenericErrorResponse & {
    error?: { message?: string; details?: unknown };
    response?: { data?: GenericErrorResponse };
  };
  const payload = err.response?.data?.error ?? err.error;
  if (!payload) return '';
  if (typeof payload.details === 'string') return payload.details;
  if (typeof payload.message === 'string') return payload.message;
  return '';
};

const displayDdiUpdateError = (error: unknown) => {
  const text = getApiErrorText(error);
  if (
    text.includes('Changing DDI subtype requires updating reportIds') ||
    text.includes('Report is not valid for the selected DDI type and subtype')
  ) {
    toast({
      description: DDI_SUBTYPE_REPORT_COMPATIBILITY_MESSAGE,
      variant: 'destructive',
      duration: 5000,
    });
    return;
  }
  displayError(error, 5000);
};

export const DDIWorkOrderEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);

  const canAccess = useMemo(
    () =>
      hasFeature(Feature.DUE_DILIGENCE) &&
      hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_EDIT) &&
      (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser)),
    [loggedInUser]
  );

  const showAssignedUserField =
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_ASSIGN) && canSelectDdiAssigneeOnForm(loggedInUser);

  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(STEPS);
  const [currentSchema, setCurrentSchema] = useState<(typeof STEP_SCHEMAS)[number]>(STEP_SCHEMAS[0]);
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [isConcurrentBlocked, setIsConcurrentBlocked] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const preserveErrorsOnStepChangeRef = useRef(false);
  const cancelledRef = useRef(false);
  const lockAcquiredRef = useRef(false);
  const mountedRef = useRef(true);
  const loadedAssignedUserIdRef = useRef<string | null>(null);

  const form = useForm<DdiCreateFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: {
      serviceType: 'Due Diligence',
      ddiType: '',
      ddiTypeId: null,
      ddiSubtype: null,
      ddiSubtypeId: null,
      subtypeRequired: false,
      subtypesLookupPending: false,
      fileNumber: '',
      clientId: null,
      clientCode: '',
      clientName: null,
      contactId: '',
      contactEmail: null,
      lawyerId: null,
      courtName: null,
      caseType: '',
      caseSubType: '',
      caseNumber: '',
      caseName: '',
      personForInquiry: null,
      entityForInquiry: null,
      vin: null,
      addressType: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      zipCode: null,
      showAlternativeAddress: false,
      alternativeAddressLine1: null,
      alternativeAddressLine2: null,
      alternativeAddressCity: null,
      alternativeAddressState: null,
      alternativeAddressZipCode: null,
      notes: null,
      sameDayRush: false,
      nextDayRush: false,
      dueDate: null,
      dueDateTouched: false,
      reportId: null,
      assignedUserId: null,
    },
  });

  const subtypesLookupPending = form.watch('subtypesLookupPending');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCurrentSchema(STEP_SCHEMAS[activeStep]);
    const preserveErrors = preserveErrorsOnStepChangeRef.current;
    preserveErrorsOnStepChangeRef.current = false;
    setTimeout(async () => {
      await form.trigger();
      if (!preserveErrors) {
        form.clearErrors();
      }
    }, 50);
  }, [activeStep]);

  const { mutate: fetchForEdit, status: fetchStatus } = useFetchDdiWorkOrderForEdit();
  const { mutate: updateWorkOrder, status: updateStatus } = useUpdateDdiWorkOrder();

  const releaseLock = useCallback((workOrderId: string) => {
    cancelDdiWorkOrderEdit(workOrderId).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!id || !canAccess) return;

    fetchForEdit(id, {
      onSuccess: (data) => {
        const ownsLock = !(data.isEditing && data.editingById !== loggedInUser?.userId);

        // Fetch completed after unmount — release lock immediately if we own it.
        if (!mountedRef.current) {
          if (ownsLock) {
            releaseLock(id);
          }
          return;
        }

        if (!ownsLock) {
          cancelledRef.current = true;
          setIsConcurrentBlocked(true);
          return;
        }

        lockAcquiredRef.current = true;
        setWorkOrderNumber(data.workOrderNumber);
        loadedAssignedUserIdRef.current = data.assignedTo?.userId ?? null;
        form.reset(mapDdiEditResponseToFormValues(data));
        setIsLoaded(true);
      },
      onError: (error) => {
        if (!mountedRef.current) return;
        if (isLockConflictError(error)) {
          cancelledRef.current = true;
          setIsConcurrentBlocked(true);
          return;
        }
        setHasFetchError(true);
        displayError(error, 5000);
      },
    });
  }, [id, canAccess]);

  const triggerCancel = useCallback(() => {
    if (!id || cancelledRef.current || !lockAcquiredRef.current) return;
    cancelledRef.current = true;
    lockAcquiredRef.current = false;
    releaseLock(id);
  }, [id, releaseLock]);

  useEffect(() => {
    return () => {
      triggerCancel();
    };
  }, [triggerCancel]);

  useEffect(() => {
    const handleBeforeUnload = () => triggerCancel();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [triggerCancel]);

  if (!canAccess) {
    return <Navigate to="/not-found" />;
  }

  if (hasFetchError) {
    return <Navigate to="/due-diligence" />;
  }

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const nextStep = async () => {
    if (form.getValues('subtypesLookupPending')) return;
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prev) => prev.map((s, i) => (i === activeStep ? { ...s, isCompleted: true } : s)));
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      setTimeout(scrollToTop, 100);
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setTimeout(scrollToTop, 100);
  };

  const saveHandler = () => {
    const values = form.getValues();
    const step1Result = DdiStep1Schema.safeParse(values);
    const step2Result = DdiStep2Schema.safeParse(values);

    if (!step1Result.success) {
      applyZodIssuesToForm(step1Result.error.issues, form.setError);
      preserveErrorsOnStepChangeRef.current = true;
      setActiveStep(0);
      setTimeout(scrollToTop, 100);
      return;
    }

    if (!step2Result.success) {
      applyZodIssuesToForm(step2Result.error.issues, form.setError);
      return;
    }

    const payload = buildDdiWorkOrderUpdatePayload(values);
    const nextAssignedUserId = values.assignedUserId ?? null;
    const loadedAssignedUserId = loadedAssignedUserIdRef.current;
    const assigneeChanged =
      showAssignedUserField && nextAssignedUserId !== loadedAssignedUserId && !!nextAssignedUserId;

    updateWorkOrder(
      { id: id!, payload },
      {
        onSuccess: async () => {
          if (assigneeChanged) {
            setIsAssigning(true);
            try {
              const { error } = await assignDdiWorkOrder(id!, nextAssignedUserId);
              if (error) {
                displayError(error, 5000);
                return;
              }
              loadedAssignedUserIdRef.current = nextAssignedUserId;
            } finally {
              setIsAssigning(false);
            }
          }

          cancelledRef.current = true;
          lockAcquiredRef.current = false;
          void queryClient.invalidateQueries({ queryKey: ['ddi-work-orders'] });
          void queryClient.invalidateQueries({ queryKey: ['ddi-work-order-detail', id] });
          toast({
            variant: 'default',
            description: 'Due Diligence work order updated successfully.',
            duration: 5000,
          });
          navigate('/due-diligence');
        },
        onError: (error) => {
          displayDdiUpdateError(error);
        },
      }
    );
  };

  if (isConcurrentBlocked) {
    return (
      <AlertBox
        title="Work Order Currently Being Edited"
        description="Another user is currently editing this work order. Please try again later."
        confirmBtnLabel="Go Back"
        confirmBtnClick={() => navigate('/due-diligence')}
        displayCancelButton={false}
        open={true}
      />
    );
  }

  return (
    <div className="w-full scrollbar-hide">
      <div className="w-full">
        <ToolBar title={workOrderNumber ? `Edit: W/O ${workOrderNumber}` : 'Edit Work Order'} />
        <Loader isLoading={fetchStatus === 'pending' || (!isLoaded && !hasFetchError)}>
          <div className="mt-3 flex justify-center pl-24">
            <Stepper steps={steps} activeStep={activeStep} goToStep={Function.prototype as (step: number) => void} />
          </div>

          <FormProvider {...form}>
            {activeStep === 0 && <DDIWorkOrderCreateStep1 isEditMode />}
            {activeStep === 1 && <DDIWorkOrderCreateStep2 showAssignedUserField={showAssignedUserField} />}
          </FormProvider>

          <div className="flex justify-end mt-4 mb-8 w-full gap-2">
            {activeStep > 0 && (
              <Button
                variant="outline"
                className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
                onClick={prevStep}
              >
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 && (
              <Button
                className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
                onClick={nextStep}
                disabled={!form.formState.isValid || !!subtypesLookupPending}
              >
                Next
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button
                className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
                onClick={saveHandler}
                disabled={!form.formState.isValid || updateStatus === 'pending' || isAssigning}
              >
                Save
              </Button>
            )}
          </div>
        </Loader>
      </div>
    </div>
  );
};

export default DDIWorkOrderEdit;
